import { ArrowUpRight } from "lucide-react";
import { TEST_IDS, PLATFORM_LINKS, ASSETS } from "@/constants/testIds";

const PlatformCard = ({
    href,
    image,
    imageAlt,
    eyebrow,
    title,
    description,
    buttonLabel,
    cardTestId,
    buttonTestId,
}) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={cardTestId}
        aria-label={`${title} — opens in a new tab`}
        className="group lift block bg-white border border-hairline overflow-hidden h-full"
    >
        <div className="relative aspect-[4/3] overflow-hidden bg-offwhite">
            <img
                src={image}
                alt={imageAlt}
                className="card-image w-full h-full object-cover object-top"
                loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/15 to-transparent pointer-events-none" />
        </div>

        <div className="p-7 md:p-9 flex flex-col gap-5">
            <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-navy font-medium mb-3">
                    {eyebrow}
                </p>
                <h3 className="font-serif text-3xl md:text-4xl tracking-tight text-ink leading-tight">
                    {title}
                </h3>
            </div>

            <p className="text-base text-muted2 leading-relaxed">{description}</p>

            <div className="pt-4 border-t border-hairline mt-2 flex items-center justify-between">
                <span
                    data-testid={buttonTestId}
                    className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase font-medium text-navy"
                >
                    {buttonLabel}
                    <ArrowUpRight
                        size={16}
                        strokeWidth={1.5}
                        className="arrow-shift"
                    />
                </span>
                <span className="text-[10px] tracking-[0.25em] uppercase text-muted2/70">
                    External
                </span>
            </div>
        </div>
    </a>
);

const Platforms = () => {
    return (
        <section
            id="platforms"
            data-testid={TEST_IDS.platformsSection}
            className="relative py-24 md:py-32 bg-offwhite"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
                    <div className="max-w-2xl">
                        <p className="text-[11px] tracking-[0.3em] uppercase text-navy font-medium mb-5">
                            Our Platforms
                        </p>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-ink">
                            Two platforms, one purpose.
                        </h2>
                    </div>
                    <p className="md:max-w-sm text-base md:text-[15px] text-muted2 leading-relaxed">
                        Each platform is owned and developed by Southern African Traders
                        Ltd, designed to connect people through language, culture and play.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-9">
                    <PlatformCard
                        href={PLATFORM_LINKS.linguisticQuest}
                        image={ASSETS.imgLinguisticQuest}
                        imageAlt="Linguistic Quest — language puzzles for families, featuring Shona, Ndebele, Yoruba, Swahili, French, Portuguese and Spanish"
                        eyebrow="Language · Culture · Learning"
                        title="Linguistic Quest"
                        description="A language and cultural platform designed to encourage learning, discovery, dialogue, and greater understanding across communities."
                        buttonLabel="Visit Linguistic Quest"
                        cardTestId={TEST_IDS.cardLinguisticQuest}
                        buttonTestId={TEST_IDS.visitLqBtn}
                    />
                    <PlatformCard
                        href={PLATFORM_LINKS.playTsoro}
                        image={ASSETS.imgPlayTsoro}
                        imageAlt="Play Tsoro — the Zimbabwean four-row mancala sowing game, with playable country variants across Africa"
                        eyebrow="Heritage · Strategy · Play"
                        title="Play Tsoro"
                        description="A digital platform inspired by the traditional African strategy game Tsoro, combining play, culture, learning, and connection."
                        buttonLabel="Visit Play Tsoro"
                        cardTestId={TEST_IDS.cardPlayTsoro}
                        buttonTestId={TEST_IDS.visitTsoroBtn}
                    />
                </div>
            </div>
        </section>
    );
};

export default Platforms;
